import * as React from 'react';

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset as floatingOffset,
  type Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Slot } from '@radix-ui/react-slot';

import { useMonkeysComponentAttributes } from '../../provider/context';
import { cn } from '../base/utils';
import { getOverlaySurfaceClassName } from './internal/overlaySurface';

interface FloatingPopoverContextValue {
  open: boolean;
  floatingStyles: React.CSSProperties;
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  context: ReturnType<typeof useFloating>['context'];
  refs: ReturnType<typeof useFloating>['refs'];
}

const FloatingPopoverContext = React.createContext<FloatingPopoverContextValue | null>(null);

export interface FloatingPopoverProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  offset?: number;
  children: React.ReactNode;
}

export function FloatingPopover({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom-start',
  offset = 8,
  children,
}: FloatingPopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = React.useCallback((next: boolean) => {
    onOpenChange?.(next);
    if (controlledOpen === undefined) setUncontrolledOpen(next);
  }, [controlledOpen, onOpenChange]);
  const { refs, floatingStyles, context } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [floatingOffset(offset), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'pointerdown' });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const value = React.useMemo(
    () => ({ open, floatingStyles, getReferenceProps, getFloatingProps, context, refs }),
    [context, floatingStyles, getFloatingProps, getReferenceProps, open, refs],
  );
  return <FloatingPopoverContext.Provider value={value}>{children}</FloatingPopoverContext.Provider>;
}

function useFloatingPopoverContext() {
  const context = React.useContext(FloatingPopoverContext);
  if (!context) throw new Error('FloatingPopover components must be used within <FloatingPopover>');
  return context;
}

export interface FloatingPopoverTriggerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
}

export const FloatingPopoverTrigger = React.forwardRef<HTMLElement, FloatingPopoverTriggerProps>(
  ({ asChild, ...props }, forwardedRef) => {
    const context = useFloatingPopoverContext();
    const Component: React.ElementType = asChild ? Slot : 'button';
    return (
      <Component
        {...context.getReferenceProps({
          ref: (node: HTMLElement | null) => {
            context.refs.setReference(node);
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          },
          type: asChild ? undefined : 'button',
          ...props,
        })}
      />
    );
  },
);
FloatingPopoverTrigger.displayName = 'FloatingPopoverTrigger';

export interface FloatingPopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  portal?: boolean;
}

export const FloatingPopoverContent = React.forwardRef<HTMLDivElement, FloatingPopoverContentProps>(
  ({ className, style, portal = true, ...props }, forwardedRef) => {
    const context = useFloatingPopoverContext();
    const componentAttributes = useMonkeysComponentAttributes();
    if (!context.open) return null;
    const floatingProps = context.getFloatingProps({
      ref: (node: HTMLDivElement | null) => {
        context.refs.setFloating(node);
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      className: cn(
        'z-50 w-72 border p-4 outline-none',
        getOverlaySurfaceClassName('menu'),
        className,
      ),
      style: { ...context.floatingStyles, ...style },
      tabIndex: -1,
      ...props,
    });
    const content = (
      <FloatingFocusManager context={context.context} modal={false} returnFocus={false}>
        <div
          {...floatingProps}
          {...componentAttributes}
          data-monkeys-component="floating-popover"
          data-slot="content"
          data-surface="menu"
        />
      </FloatingFocusManager>
    );
    return portal ? <FloatingPortal>{content}</FloatingPortal> : content;
  },
);
FloatingPopoverContent.displayName = 'FloatingPopoverContent';
